import type { CreateAutopilotDto } from 'openapi/models/CreateAutopilotDto'
import type { AutopilotDirection, AutopilotFrequency } from '../types'

export type AutopilotChannelId = CreateAutopilotDto['channel']

/**
 * Default publish hour (UTC). Mirrors `DEFAULT_PUBLISH_HOUR_UTC` in
 * `apps/server/src/autopilot/utils/autopilot-slots.util.ts` — keep them in
 * sync. Until per-business timezones land, noon UTC keeps publish times in
 * the daytime band for the widest range of timezones.
 */
export const DEFAULT_PUBLISH_HOUR_UTC = 12

/** Normalize an ISO date to its UTC-midnight ISO string (the day-bucket key). */
export function toDayKeyIso(input: string | Date): string {
  const d = typeof input === 'string' ? new Date(input) : new Date(input)
  d.setUTCHours(0, 0, 0, 0)
  return d.toISOString()
}

/** ISO timestamp at noon UTC on the same UTC date as `input`. */
export function toNoonUtcIso(input: string | Date): string {
  const d = typeof input === 'string' ? new Date(input) : new Date(input)
  d.setUTCHours(DEFAULT_PUBLISH_HOUR_UTC, 0, 0, 0)
  return d.toISOString()
}

/** Whole-day offset between two ISO date strings, computed in UTC. */
export function dayOffsetUtc(iso: string | null, anchor: string | null): number | null {
  if (!iso || !anchor) return null
  const a = new Date(iso)
  const b = new Date(anchor)
  a.setUTCHours(0, 0, 0, 0)
  b.setUTCHours(0, 0, 0, 0)
  const dayMs = 24 * 60 * 60 * 1000
  return Math.round((a.getTime() - b.getTime()) / dayMs)
}

/**
 * Single source of truth for the four content directions the autopilot
 * supports. Mirrors the server's `AUTOPILOT_DIRECTIONS` allow-list (kept in
 * sync manually until codegen covers enums) and is used by the popover
 * (multi-select) plus the per-card direction override flow.
 */
export const AUTOPILOT_DIRECTION_VALUES = ['products', 'expertise', 'updates', 'custom'] as const
export type AutopilotDirectionValue = (typeof AUTOPILOT_DIRECTION_VALUES)[number]

/**
 * Display options for the four content directions — emoji + label. Shared by
 * the defaults popover (multi-select) and the weekly-plan setup wizard so both
 * surfaces show the exact same chips in the same order.
 */
export const AUTOPILOT_DIRECTION_OPTIONS: {
  id: AutopilotDirection
  emoji: string
  label: string
}[] = [
  { id: 'expertise', emoji: '💡', label: 'Tips' },
  { id: 'products', emoji: '🛍️', label: 'Promote' },
  { id: 'updates', emoji: '📢', label: 'Update' },
  { id: 'custom', emoji: '✏️', label: 'Custom' },
]

/**
 * Cadence presets shown in the setup wizard's "When" step. The authoritative
 * scheduling input is the selected weekday set (`preferredDays`) — these
 * presets just pre-fill that set. `custom` selects no fixed days; the weekday
 * grid drives it. Biweekly/monthly were dropped from the product.
 */
export const AUTOPILOT_FREQUENCY_OPTIONS: { id: AutopilotFrequency; label: string }[] = [
  { id: 'daily', label: 'Daily' },
  { id: '3x_weekly', label: '3×/week' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'custom', label: 'Custom' },
]

/**
 * The seven weekdays in display order (Mon→Sun), with the lowercase names the
 * server stores in `preferredDays`. Single source of truth for the wizard's
 * weekday grid.
 */
export const WEEKDAY_OPTIONS: { id: string; short: string; label: string }[] = [
  { id: 'monday', short: 'M', label: 'Mon' },
  { id: 'tuesday', short: 'T', label: 'Tue' },
  { id: 'wednesday', short: 'W', label: 'Wed' },
  { id: 'thursday', short: 'T', label: 'Thu' },
  { id: 'friday', short: 'F', label: 'Fri' },
  { id: 'saturday', short: 'S', label: 'Sat' },
  { id: 'sunday', short: 'S', label: 'Sun' },
]

const ALL_WEEKDAYS = WEEKDAY_OPTIONS.map(d => d.id)

/** Weekday set each non-custom preset fills in. */
export const PRESET_DAYS: Record<Exclude<AutopilotFrequency, 'custom'>, string[]> = {
  daily: ALL_WEEKDAYS,
  '3x_weekly': ['monday', 'wednesday', 'friday'],
  weekly: ['monday'],
  // Legacy presets — retained so existing plans still map to a day set.
  biweekly: ['monday'],
  monthly: ['monday'],
}

/**
 * Derive the coarse `frequency` label from a selected weekday set, for
 * analytics/back-compat. `preferredDays` remains the authoritative schedule.
 */
export function frequencyFromDays(days: string[]): AutopilotFrequency {
  if (days.length === 7) return 'daily'
  if (days.length === 1) return 'weekly'
  if (days.length === 3 && ['monday', 'wednesday', 'friday'].every(d => days.includes(d)))
    return '3x_weekly'
  return 'custom'
}

/** Order an arbitrary weekday set into Mon→Sun display order. */
export function sortWeekdays(days: string[]): string[] {
  return ALL_WEEKDAYS.filter(d => days.includes(d))
}

/** Format an "HH:mm" (24h) value to a friendly 12h label, e.g. "9:00 AM". */
export function formatTimeLabel(value: string): string {
  const [h, m] = value.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return value
  const period = h < 12 ? 'AM' : 'PM'
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`
}

/** Human summary of the selected weekday set, e.g. "Mon, Wed, Fri" / "every day". */
export function daysSummary(days: string[]): string {
  if (days.length === 7) return 'every day'
  if (days.length === 0) return 'no days yet'
  return sortWeekdays(days)
    .map(d => WEEKDAY_OPTIONS.find(w => w.id === d)?.label ?? d)
    .join(', ')
}

/**
 * Single source of truth for the autopilot channels we render in the UI.
 * Scoped to the platforms a static image can publish to (autopilot only
 * generates static-image posts) — TikTok/YouTube need video and X isn't wired
 * yet, so they're intentionally omitted. Add a channel by extending this list
 * once its publish path is ready.
 */
export const AUTOPILOT_CHANNEL_OPTIONS: readonly {
  id: AutopilotChannelId
  label: string
}[] = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'pinterest', label: 'Pinterest' },
]

/** Display label by autopilot/queue channel. Falls back to the raw id. */
export const CHANNEL_LABEL: Record<string, string> = Object.fromEntries(
  AUTOPILOT_CHANNEL_OPTIONS.map(o => [o.id, o.label])
)

/**
 * Channels that map 1:1 to an OAuth provider we can publish through. Used by
 * the Weekly Cockpit to (a) decide when to surface the post-approve "connect
 * your platform" prompt and (b) detect scheduled posts whose channel isn't
 * connected, so we can warn before they silently fail to publish. Non-social
 * channels (e.g., email) don't need OAuth.
 */
export const CONNECTABLE_CHANNELS = [
  'instagram',
  'facebook',
  'linkedin',
  'tiktok',
  'youtube',
  'pinterest',
] as const

export type ConnectableChannel = (typeof CONNECTABLE_CHANNELS)[number]

const CONNECTABLE_CHANNEL_SET = new Set<string>(CONNECTABLE_CHANNELS)

export function isConnectableChannel(channel: string): channel is ConnectableChannel {
  return CONNECTABLE_CHANNEL_SET.has(channel)
}

/**
 * Connection-row label for each connectable channel. Falls back to the raw id
 * when an unknown channel slips through, but every entry in
 * CONNECTABLE_CHANNELS has an explicit label here so capitalization
 * (YouTube, TikTok) is consistent in the UI.
 */
export const CONNECTABLE_CHANNEL_LABEL: Record<ConnectableChannel, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
}
